import { FC } from 'react';
import { CardContent } from '@mui/material';
import { Typography } from '@wellstone-solutions/web';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@components/ui';
import { CardHeader } from '@components/ui/Card';

import type { ForgotPasswordMutationFunction } from './types';

interface ForgotPasswordSuccessProps {
  email?: string;
  resend: ForgotPasswordMutationFunction;
  setForceSuccess: (val: boolean) => void;
}
const ForgotPasswordSuccess: FC<ForgotPasswordSuccessProps> = ({
  resend,
  email,
  setForceSuccess,
}) => {
  const { t } = useTranslation();

  const handleResend = () => {
    if (email) {
      setForceSuccess(true);
      resend({ email });
    }
  };

  return (
    <>
      <CardHeader
        title={t('auth.reset_password.title')}
        subheaders={[
          <Trans
            key="auth.reset_password.success_subheader"
            i18nKey="auth.reset_password.success_subheader"
            values={{ email: email ?? 'your email' }}
            defaultValue="We've sent a reset link to {{email}}. Please check your inbox and tap the link to verify your account."></Trans>,
        ]}></CardHeader>
      <CardContent sx={styles.cardContent}>
        <Typography fontWeight="bold" color="text.secondary">
          <Trans
            i18nKey="auth.verify.expiry"
            defaultValue="👉 Heads up! The link will expire in 15 minutes."
          />
        </Typography>
        <Typography color="text.secondary">
          <Trans
            i18nKey="auth.verify.no_email"
            components={{ strong: <strong /> }}
            defaultValue="🤨 <strong>Didn't get the email?</strong> Check your spam or junk folder, or we can resend you a new email."
          />
        </Typography>
        <Button
          variant="outlined"
          onClick={handleResend}
          sx={styles.resendButton}>
          <Trans i18nKey="auth.verify.resend" defaultValue="Resend email" />
        </Button>
      </CardContent>
    </>
  );
};

const styles = {
  cardContent: {
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.625rem',
  },
  resendButton: { alignSelf: 'center', width: 'auto' },
};

export { ForgotPasswordSuccess };
